import { PROP } from './mech-kit';
import { POSE, STANCE, Spring, clamp, dampTo, legIK, wrapAng } from './mech-pose';
import { updateIdle } from './mech-idle';
import { buildMechFX, puffAtMuzzle, puffAtVents, updateMechFX, updatePuffs } from '../render/mech-fx';
/* ============================================================
   機體動畫（移植 `mech_sample_ani.html` §8/§10/§12，並依遊戲功能擴充）
   ============================================================

   ## 分層（與原型相同）

     基準 POSE
       + 移動層（走路相位 → 腳目標位置 → IK）
       + 扭腰層（軀幹追瞄準、腿慢半拍追軀幹）
       + 物理層（加速度 → 傾斜 / 懸吊）
       + 動作層（後座、蓄力、開架、排氣、受傷、倒地）

   ## 這一版與原型的三個差別（都是「遊戲有、原型沒有」造成的）

   **一、原型自己算位移，遊戲不行。**
   原型是「機體原地不動、地板往後捲」，所以它自己積分速度。
   遊戲裡位置歸模擬層管，所以動畫層改成**觀察者**：讀實際速度，
   對時間微分得到加速度，再拿加速度去驅動傾斜（原型的決策 7 不變 ——
   傾斜綁加速度而不是速度，那是衝刺看起來有重量的原因）。

   **二、遊戲的最高速度遠超過腿走得動的範圍。**
   原型的走路循環最快只到 2.11 單位/秒（那是刻意的慢走），
   而 CHASSIS-0 的基礎移動速度是 11.5 遊戲單位/秒 ≈ 9.75 原型單位/秒。
   硬套會滑步（步幅×步頻對不上地面速度，也就是原型最在意的那個不變式）。
   作法：**半速以下走路、半速以上滑行**。走路段的步頻由實際速度**反解**
   （`solveWalk`），所以腳在那一段永遠不打滑；超過之後腿收起往後拖、
   噴焰加大 —— 那本來就是原型 `air` 狀態的姿勢，而這台機體本來就有推進器。
   → `WALK_BAND` 是這件事唯一的調參面，覺得走太少就調高它。

   **三、原型的熱量系統沒有移植。**
   排氣的**動畫**留著（很好看），但不綁「會自己漲、漲滿罰你」的儀表 ——
   那種累積型資源條在這個專案被否決過（會讓人覺得是作業不是能力）。
   改成事件驅動：大招發射、受傷、復活各觸發一次散熱。 */

/** 走路段佔最高速度的比例。以下走路、以上滑行。 */
var WALK_BAND = 0.55;
/** 原型的步幅×步頻曲線在 s = 1.45 時的地面速度（原型單位/秒）。 */
var WALK_S_MAX = 1.45;

function vWalk(s) { return (0.30 + 0.66 * s) * ((1.05 + s * 1.15) * s); }
/** 反解「要多快的步速參數才會等於這個地面速度」。
    步幅×步頻 = 地面速度是原型的核心不變式，反解就不會有滑步。 */
function solveWalk(v) {
  if (v <= 0) return 0;
  var lo = 0, hi = WALK_S_MAX;
  if (v >= vWalk(hi)) return hi;
  for (var i = 0; i < 14; i++) { var m = (lo + hi) * 0.5; if (vWalk(m) < v) lo = m; else hi = m; }
  return (lo + hi) * 0.5;
}
function smooth01(x, a, b) { var t = clamp((x - a) / (b - a), 0, 1); return t * t * (3 - 2 * t); }

/** 每台機體一份。狀態掛在 rig 上，**不要放模組層級** ——
    那會讓下一場開局帶著上一場的殘留（旋刃那個坑記過一次）。 */
export function newAnim() {
  return {
    t: 0, phase: 0, speedS: 0, glide: 0, moveYaw: 0, prevSpeed: 0, acc: 0,
    thrust: 0, flash: 0, charge: 0, vent: 0, ventT: 0, dead: 0, flare: 0, wasAir: false,
    /* `swing` 0→1 的揮擊殘量；`swingDir` ±1 讓連續兩刀反手（不然兩下一模一樣）；
       `swingChop` 1 = 下劈（連段第三段與收刀），0 = 橫掃。 */
    swing: 0, swingDir: 1, swingChop: 0,
    twist: new (Spring as any)(60, 11),
    susp: new (Spring as any)(150, 17),   // 垂直懸吊：落地壓縮 / 回彈
    lean: new (Spring as any)(70, 12),    // 前後傾：由加速度驅動
    roll: new (Spring as any)(55, 11),
    crouch: new (Spring as any)(70, 13),
    recoil: new (Spring as any)(230, 18),
    headS: new (Spring as any)(90, 13),
    pylonS: new (Spring as any)(60, 12)
  };
}

/* ── 一次性事件 ────────────────────────────────────────────────
   模擬層在事件發生的那一刻推一次，動畫層負責收斂。
   一個彈簧散到四個部位（原型 §8.6），所以收斂時間自然一致、不會不同步。 */
export function mechEvent(rig, kind, arg?) {
  if (!rig || rig.kind !== 'v3') return;
  var A = rig.anim; if (!A) return;
  /* ── 近戰機體（LANCER-2）：同樣三個按鍵，反饋完全不同 ──────────
     射擊型的回饋是**後座**（被自己的槍推回來），近戰型的是**揮擊**
     （自己把質量甩出去）。共用 `mechEvent` 是刻意的：模擬層只知道
     「fire / mid / ult 發生了」，該長什麼樣是動畫層的事。 */
  if (rig.melee) {
    /* 每揮一刀就換一次手方向 —— 兩下一模一樣的橫掃讀起來像卡帶。 */
    if (kind === 'fire') {
      A.swing = 1; A.swingDir = -A.swingDir; A.swingChop = 0;
      A.recoil.kick(2.2); A.lean.kick(1.1); return;
    }
    /* 下劈：連段第三段、居合收刀、旋刃收束共用。**軌跡與橫掃完全不同**
       （由上往下、不橫向掃），所以第三下在畫面上是看得出來的另一個動作。 */
    if (kind === 'heavy') {
      A.swing = 1; A.swingChop = 1;
      A.recoil.kick(7); A.lean.kick(3.4); A.susp.kick(-1.6);
      A.flash = 1; A.flare = 0.6;
      return;
    }
    if (kind === 'mid') {                     // 突進斬：整台往前撲
      A.swing = 1; A.recoil.kick(5); A.lean.kick(4.2); A.susp.kick(-1.2);
      A.flash = 1; puffAtVents(rig, 0.8); return;
    }
    if (kind === 'ult') {                     // 旋刃：蹲身開架 + 全開散熱
      A.swing = 1; A.recoil.kick(9); A.susp.kick(-2.2); A.pylonS.kick(7);
      A.flash = 1; A.flare = 1; A.ventT = Math.max(A.ventT, 1.1);
      puffAtVents(rig, 1.2); return;
    }
  }
  if (kind === 'fire') {
    /* 主砲 2026-08-05 改成 **3.75 發/秒、單發傷害加倍**（`entities/mechs.ts`）。
       後座 3.2 → 5.4：發數減半而每一發更重，維持原本的小後座會讓它讀起來
       像「射速變慢了」而不是「每一發更有份量」。
       噴煙也不再隔次跳過 —— 那個節流是為 7.5 發/秒設計的（不跳過會滿螢幕是煙），
       現在的頻率每一發都噴剛好。 */
    A.recoil.kick(5.4); A.flash = 1;
    puffAtMuzzle(rig, false);
  } else if (kind === 'mid') {                 // 飛彈齊射：掛架開架 + 中後座
    A.recoil.kick(9); A.flash = 1; A.pylonS.kick(6);
    puffAtMuzzle(rig, false); puffAtVents(rig, 0.5);
  } else if (kind === 'ult') {                 // 大招：被自己的後座推退
    A.recoil.kick(22); A.flash = 1; A.susp.kick(-2.6);
    A.flare = 1; A.ventT = Math.max(A.ventT, 0.9);
    puffAtMuzzle(rig, true); puffAtVents(rig, 1.2);
  } else if (kind === 'hurt') {                // 受傷：被打的方向頂一下
    A.susp.kick(-1.9); A.recoil.kick(4.5);
    A.lean.kick(-2.2); A.flare = 0.7;
  } else if (kind === 'revive') {              // 復活：蹲下彈起 + 全開散熱
    A.susp.kick(5.5); A.flare = 1; A.ventT = 1.2; A.dead = 0;
    puffAtVents(rig, 1.2);
  } else if (kind === 'levelup') {
    A.flare = 0.85; A.susp.kick(1.4);
  } else if (kind === 'lock') {                // 鎖定：頭部先甩過去
    A.headS.kick(arg ? clamp(arg * 5, -6, 6) : 3);
  } else if (kind === 'land') {
    A.susp.kick(-2.8);
  }
}

/** 展示用站姿（開始畫面與機庫）。動畫層仍然每幀跑，這裡只是把
    「上一場陣亡當下的姿勢」清掉 —— 不清的話機體會保持倒地的樣子站在展示台上。 */
export function restMechPose(rig, aimYaw, moveYaw) {
  if (!rig) return;
  rig.root.position.set(0, 0, 0); rig.root.rotation.y = 0;
  rig.anim = newAnim();
  rig.anim.moveYaw = moveYaw;
  rig.body.visible = true;
}

/** 每幀。`IN` 是模擬層的觀測值，見檔頭第一點：動畫層是觀察者不是決策者。

    IN = { speed, speedMax, aimYaw, moveYaw, dash, ult, dead, hidden, idle }

    `idle` = 展示模式（機庫／開始畫面）：加一層怠速表演，見 `mech-idle.ts`。 */
export function updateMechAnim(rig, dt, IN) {
  if (!rig || dt <= 0) return;
  updatePuffs(dt);
  var A = rig.anim; if (!A) return;
  /* **一律讀 rig 上的三組常數，不要讀模組層級的 POSE/STANCE。**
     那是兩台機體共用這一整層動畫的唯一理由（`entities/mechs.ts` 的說明）。 */
  var PS = rig.pose || POSE, ST = rig.stance || STANCE, IKC = rig.ik;
  A.t += dt;

  /* --- 速度 → 步速參數 / 滑行量 / 加速度 --- */
  var vDemo = IN.speed / PROP.scale;
  var band = (IN.speedMax / PROP.scale) * WALK_BAND;
  A.speedS = dampTo(A.speedS, solveWalk(Math.min(vDemo, band)), 14, dt);
  /* 真的離地時直接用滑行姿勢（腿收起往後拖）—— 那本來就是原型 `air` 狀態的
     姿態，而現在機體是真的在空中，用走路循環會變成「在空中踏步」。 */
  A.glide = dampTo(A.glide, IN.air ? 1 : smooth01(IN.speed, IN.speedMax * WALK_BAND, IN.speedMax * 1.02), 9, dt);
  // 對時間微分得到加速度；平滑一下，否則單一幀的抖動會被放大成抽搐
  A.acc = dampTo(A.acc, (vDemo - A.prevSpeed) / dt, 18, dt);
  A.prevSpeed = vDemo;
  var air = A.glide > 0.35;
  if (A.wasAir && !air) A.susp.kick(-2.8);      // 落地：懸吊壓縮
  A.wasAir = air;

  /* --- 一次性動作的殘量 --- */
  A.flash = Math.max(0, A.flash - dt * 9);
  A.flare = Math.max(0, A.flare - dt * 2.6);
  A.ventT = Math.max(0, A.ventT - dt);
  A.vent = dampTo(A.vent, A.ventT > 0 ? 1 : 0, A.ventT > 0 ? 14 : 3.2, dt);
  if (A.ventT > 0 && (((A.ventT / 0.14) | 0) !== (((A.ventT + dt) / 0.14) | 0))) puffAtVents(rig, 0.55);
  A.charge = dampTo(A.charge, IN.ult || 0, 10, dt);
  /* 近戰揮擊。`arc` 是 0→1→0 的單峰：揮出去再收回來，一次 0.17 秒
     （主武裝 2.7 發/秒＝每 0.37 秒一刀，收得回來才不會黏成一團）。 */
  A.swing = Math.max(0, A.swing - dt * 6);
  var arc = A.swing > 0 ? Math.sin(Math.PI * A.swing) : 0;
  A.dead = dampTo(A.dead, IN.dead ? 1 : 0, IN.dead ? 3.4 : 9, dt);

  /* --- 待機層。放在殘量之後、姿態之前：它推的 ventT 與彈簧 kick 走既有管線，
         回傳的偏移由下面各處自己加。對局中 `IN.idle` 是 falsy，整層為 0。 --- */
  var ID = updateIdle(A, dt, IN.idle ? 1 : 0);

  /* --- 走路相位。步幅×步頻 = 地面速度，所以腳不打滑 --- */
  A.phase = (A.phase + dt * (1.05 + A.speedS * 1.15) * A.speedS) % 1;

  /* --- 扭腰：軀幹追瞄準，腿慢半拍追軀幹 --- */
  var lim = 0.55, d = wrapAng(IN.aimYaw - A.moveYaw);
  if (Math.abs(d) > lim) A.moveYaw = wrapAng(A.moveYaw + (Math.abs(d) - lim) * (d > 0 ? 1 : -1) * 5.0 * dt);
  // 有在移動時，下盤直接追移動方向（那是玩家的操作），停下來才慢慢追瞄準
  if (A.speedS > 0.05 || A.glide > 0.05) A.moveYaw = wrapAng(A.moveYaw + wrapAng(IN.moveYaw - A.moveYaw) * 12 * dt);
  else A.moveYaw = wrapAng(A.moveYaw + wrapAng(IN.aimYaw - A.moveYaw) * 1.7 * dt);
  A.twist.to(clamp(wrapAng(IN.aimYaw - A.moveYaw), -0.95, 0.95), dt);

  /* --- 身體：蹲下 / 懸吊 / 走路上下起伏 --- */
  /* 倒地：**靠「沉下去」而不是「往前倒」**。前傾給太多會整台趴平，
     讀起來像穿模不像陣亡；主要位移交給 crouch（髖直接掉下去），
     前傾只留一點點讓它是「單膝跪倒」而不是「原地縮小」。 */
  A.crouch.to(A.charge * 0.30 + A.dead * 0.78, dt);
  A.susp.to(0, dt);
  var bob = A.speedS * 0.045 * (1 - Math.cos(4 * Math.PI * A.phase)) * 0.5;
  rig.body.position.y = ST.bodyY + A.susp.x * 0.09 - A.crouch.x - bob + A.glide * 0.20 + ID.y;

  var rc = A.recoil.x;
  A.lean.to(clamp(A.acc * 0.0042, -0.42, 0.42) + A.speedS * 0.05 + A.glide * 0.16 + A.charge * 0.12 + A.dead * 0.30, dt);
  A.roll.to(-PS.pelvisTilt * 0.45 + A.twist.x * 0.05 + A.dead * 0.22, dt);
  /* 軀幹的 yaw = `moveYaw + twist`，而 twist 是彈簧追 `aim − move` ——
     所以結果收斂到瞄準方向，但**帶著彈簧的延遲與過衝**，那就是扭腰的手感。
     直接寫 `= aimYaw` 的話軀幹會硬鎖在砲口方向，扭腰整個消失。 */
  rig.torso.rotation.set(PS.torsoPitch + A.lean.x + rc * 0.05 - A.charge * 0.05
    - arc * (0.10 + 0.26 * A.swingChop) + ID.pitch,
    A.moveYaw + A.twist.x, A.roll.x + ID.roll);
  /* 加回 `POSE.hipYaw`：髖線與肩線交叉 11° 是設計文件 §5 明列的姿態特徵，
     但原型的 animate() 直接覆寫成 `= A.moveYaw`，把它蓋掉了（造型是 1:1，
     這是原型動畫層的疏漏）。要退回原型行為就把 `+ POSE.hipYaw` 拿掉。 */
  rig.lower.rotation.y = A.moveYaw + PS.hipYaw * (1 - A.glide);

  /* --- 腿：腳的位置是輸入，關節角由 IK 反推 --- */
  var hipY = rig.body.position.y, liftL = 0, liftR = 0;
  for (var i = 0; i < 2; i++) {
    var L = rig.legs[i], sd = L.side;
    var ph = (A.phase + (sd > 0 ? 0 : 0.5)) % 1;
    var fz, fy;
    if (A.speedS > 0.01) {
      var stride = PS.stride + A.speedS * PS.strideK, hgt = 0.09 + A.speedS * 0.26;
      if (ph < 0.62) { fz = stride * (0.5 - ph / 0.62); fy = 0; }          // 支撐相 62%
      else { var u = (ph - 0.62) / 0.38; fz = stride * (u - 0.5); fy = hgt * Math.sin(Math.PI * u); }
    } else {
      fz = sd > 0 ? -0.10 : 0.12;                 // 待機：重心偏左腳的錯步站姿
      fy = 0;
    }
    fy += A.glide * 0.55; fz -= A.glide * 0.50;   // 滑行：收腿往後拖
    fy += A.charge * 0.02;
    if (sd > 0) liftL = fy; else liftR = fy;
    var q = legIK(fy + ST.ankle - hipY, fz, IKC);
    L.thigh.rotation.x = q.hip;
    L.shin.rotation.x = q.knee;
    L.foot.rotation.x = -(q.hip + q.knee) + Math.min(1, fy * 3.4) * 0.30;  // 抬腳時壓趾
  }
  rig.pelvis.rotation.z = PS.pelvisTilt * (A.speedS < 0.02 ? 1 : 0.35) + (liftR - liftL) * 0.5 + ID.pelvis;

  /* --- 頭：補完軀幹轉不到的角度；倒地時垂下 --- */
  A.headS.to(clamp(wrapAng(IN.aimYaw - rig.torso.rotation.y), -0.55, 0.55), dt);
  rig.head.rotation.set(PS.headPitch - A.lean.x * 0.35 + A.charge * 0.10 + A.dead * 0.45 + ID.headPitch,
    PS.headYaw * 0.2 + A.headS.x + ID.headYaw, -A.roll.x * 0.7);

  /* --- 手臂：後座 + 大招蓄力抬臂（一個彈簧散到四處） --- */
  /* 揮擊同時動三個軸：抬臂（x）、橫掃（y）、前臂甩出（fore）。
     只動一個軸的話刀是「戳」出去的，讀不出重量。

     **橫掃與下劈是兩條不同的軌跡**：橫掃主要動 y（左右掃）、下劈主要動 x
     （由上往下）並帶軀幹前壓。連段第三段用下劈，所以「這一下不一樣」
     是看得出來的，不只是數字比較大。 */
  var chop = A.swingChop, sweep = 1 - chop;
  rig.arm.up.rotation.set(
    PS.armPitch - A.charge * 0.24 + rc * 0.10 + ID.arm - arc * (1.45 * sweep + 2.35 * chop),
    PS.armYaw + arc * 0.85 * sweep * A.swingDir,
    PS.armRoll - A.charge * 0.10 - arc * (0.30 * sweep + 0.12 * chop) * A.swingDir);
  rig.arm.fore.rotation.x = PS.forePitch + rc * 0.28 + A.charge * 0.18 - ID.arm * 0.6
    - arc * (0.55 * sweep + 1.15 * chop);
  rig.arm.aim.rotation.z = PS.aimConverge - rc * 0.05;

  /* --- 掛架：大招與飛彈齊射時開架 --- */
  A.pylonS.to(A.charge, dt);
  rig.pylon.rotation.set(-A.pylonS.x * 0.18 - arc * 0.16, PS.pylonYaw, PS.pylonRoll - A.pylonS.x * 0.44);
  rig.back.rotation.x = A.vent * 0.26 + A.charge * 0.10;   // 排氣時背包掀開

  /* --- 發光：蓄力、排氣、事件閃光都寫進同一顆材質。
         **一律寫絕對值不做累加**，所以疊在一起也不會漂移。 --- */
  var gm = rig.glow;
  gm.emissiveIntensity = gm.userData.base * (1 - A.dead * 0.92) *
    (1 + 1.6 * A.charge + 1.3 * A.vent + 1.5 * A.flare + 0.06 * Math.sin(A.t * 3.1));

  /* --- 噴焰：走路小、滑行中、衝刺全開 --- */
  var thT = Math.max(IN.dash ? 1 : 0, IN.fly || 0, A.glide * 0.85, A.speedS * 0.22, A.vent * 0.6, A.charge * 0.75,
    0.05 + 0.018 * Math.sin(A.t * 2.7)) * (1 - A.dead);
  A.thrust = dampTo(A.thrust, thT, 12, dt);
  updateMechFX(rig, A.t, A.thrust, A.flash);
  rig.body.visible = !IN.hidden;
}

export { buildMechFX };
